<template>
  <div>
    <v-card-title>
      <v-row>
        <v-col
          v-if="config.title"
          class="title"
        >
          <universal-button
            v-if="config.showBackButton && !createMode"
            type="back"
            class="mr-2"
            @click="onBack"
          />
          {{ config.title }}
        </v-col>

        <v-col
          class="d-flex justify-end"
          cols="2"
        >
<!--          Additional buttons, placed before edit buttons (if exists)-->
          <template
            v-if="canShowAdditionalButtons()"
          >
            <template
              v-for="(button, buttonIndex) in config.additional_buttons"
            >
              <div
                v-if="canShowAdditionalButton(button)"
                :key="buttonIndex"
                :style="{ cursor: permissions && !permissions.canEdit ? 'not-allowed' : 'default' }"
              >
                <!--                button with dropdown menu-->
                <template
                  v-if="button.type === 'dropdown' && button.component"
                >
                  <v-menu
                    offset-y
                    :content-class="button.menuContentClass || ''"
                    :close-on-content-click="!!button.closeOnContentClick"
                  >
                    <template v-slot:activator="{ on }">
                      <div
                        v-on="on"
                      >
                        <universal-button
                          :type="button && button.buttonType ? button.buttonType : 'base'"
                          :prepend-icon="getPrependIcon(button)"
                          :append-icon="getAppendIcon(button)"
                          :show-loader="button.showLoader !== undefined ? button.showLoader : false"
                          :label="button && button.label && !$vuetify.breakpoint.mobile ? button.label : ''"
                          :disabled="(permissions && !permissions.canEdit) || (button.disabled !== undefined ? button.disabled : false)"
                          :color="button.color || null"
                          :class="button.class || ''"
                          class="mr-2"
                        />
                      </div>
                    </template>
                    <component
                      :is="button.component"
                      :edit="edit"
                      :create-mode="createMode"
                      :form-data="formData"
                      :config="button"
                    />
                  </v-menu>
                </template>
                <!--                base button-->
                <template
                  v-else
                >
                  <universal-button
                    :type="button && button.type ? button.type : 'base'"
                    :prepend-icon="getPrependIcon(button)"
                    :append-icon="getAppendIcon(button)"
                    :show-loader="button.showLoader !== undefined ? button.showLoader : false"
                    :label="button && button.label && !$vuetify.breakpoint.mobile ? button.label : ''"
                    :disabled="(permissions && !permissions.canEdit) || (button.disabled !== undefined ? button.disabled : false)"
                    :color="button.color || null"
                    :class="button.class || ''"
                    class="mr-2"
                    @click="onAdditionalButtonClick(button)"
                  />
                </template>
              </div>
            </template>
          </template>

<!--          Edit buttons, controls visibility via config parameter 'showEditButtons'-->
<!--          Cant be configurable for now, since we want consistency, to these edit buttons be the same everywhere in app-->
          <div
            v-if="config && config.showEditButtons && config.showEditButtons === true"
            :style="{ cursor: permissions && !permissions.canEdit ? 'not-allowed' : 'default' }"
          >
<!--            Edit button-->
            <universal-button
              v-if="!edit && !createMode"
              type="edit"
              :disabled="permissions && !permissions.canEdit"
              @click="$emit('set-edit', true)"
            />
          </div>

<!--          Cancel tab edit button-->
          <universal-button
            v-if="config.showEditButtons === true && (edit || createMode)"
            class="mr-2"
            type="cancel"
            @click="onCancelTabSave"
          />

<!--          Save tab data button-->
          <universal-button
            v-if="config.showEditButtons === true && (edit || createMode)"
            @click="$emit('save-tab')"
          />
        </v-col>
      </v-row>
    </v-card-title>

    <information-dialog
      v-if="showChangesNotSavedDialog"
      :open-dialog="showChangesNotSavedDialog"
      :title="$t('base.warning')"
      :dialog-message="$t('fleet/vehicle.data_not_saved_message')"
      :positive-button-text="$t('base.confirm')"
      :negative-button-text="$t('base.cancel')"
      :confirm-callback-function="redirect"
      @close="showChangesNotSavedDialog = false"
    />
  </div>
</template>

<script>
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import InformationDialog from './dialogs/InformationDialog.vue'

export default {
  name: 'TabActionButtonsWithTitle',

  components: {
    InformationDialog,
    UniversalButton
  },

  props: {
    edit: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Object,
      default: null
    },
    createMode: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    fetchTabData: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      showChangesNotSavedDialog: false
    }
  },

  methods: {
    canShowAdditionalButton (button) {
      const { visible } = button || {}
      let canShow = visible

      if (visible === undefined) {
        return true
      }

      if (typeof visible === 'function') {
        canShow = visible({
          formData: this.formData,
          permissions: this.permissions
        })
      }

      return !!canShow
    },

    canShowAdditionalButtons () {
      const { forceAdditionalButtonsDisplay } = this.config || {}
      return this.config && this.config.additional_buttons && this.config.additional_buttons.length && (forceAdditionalButtonsDisplay || (!this.edit && !this.createMode))
    },

    onAdditionalButtonClick (button) {
      const { click } = button

      if (click && typeof click === 'function') {
        click({
          instance: this,
          formData: this.formData,
          updateData: this.updateData,
          fetchTabData: this.fetchTabData,
          permissions: this.permissions
        })
      }
    },

    onCancelTabSave () {
      const { onCancelTabSave } = this.config || {}

      if (typeof onCancelTabSave === 'function') {
        onCancelTabSave({
          formData: this.formData,
          updateData: (val) => this.updateData(val),
          cancelTabSave: () => this.cancelTabSave()
        })
        return
      }

      this.$emit('cancel-tab-save')
    },

    cancelTabSave () {
      this.$emit('cancel-tab-save')
    },

    onBack () {
      if (this.createMode || !this.edit) {
        const { onGoBack } = this.config?.showBackButton || {}

        if (typeof onGoBack === 'function') {
          onGoBack()
        }
        else {
          this.$router.replace({ name: this.config.showBackButton.uiViewName })
        }
      }
      else {
        this.showChangesNotSavedDialog = true
      }
    },

    redirect () {
      const { uiViewName, onGoBack } = this.config?.showBackButton || ''

      if (uiViewName && typeof uiViewName === 'string') {
        this.$router.replace({ name: uiViewName })
      }
      else if (typeof onGoBack === 'function') {
        onGoBack()
      }
    },

    getPrependIcon (button) {
      const { prependIcon } = button || {}
      let returnVal = prependIcon

      if (typeof prependIcon === 'function') {
        returnVal = prependIcon({
          data: this.formData
        })
      }

      return returnVal && typeof returnVal === 'string' ? returnVal : ''
    },

    getAppendIcon (button) {
      const { appendIcon } = button || {}
      let returnVal = appendIcon

      if (typeof appendIcon === 'function') {
        returnVal = appendIcon({
          data: this.formData
        })
      }

      return returnVal && typeof returnVal === 'string' ? returnVal : ''
    }
  }
}
</script>

<style scoped>
.title {
  font-family: 'Satoshi', sans-serif !important;
  font-size: 20px !important;
  font-weight: 700;
  color: #111827;
  line-height: 28px;
  display: flex;
  justify-content: start;
  align-items: center;
}
</style>
